/**
 * Offset grid design (research/taxonomy.md §1.3): full-width shelves, short dividers whose
 * x positions change row to row (brick-bond, alternating or free). The row-major layout mode
 * of the shared case generator; the parameter block lives in `design.params`.
 */
import type { Appearance, Design } from '../../domain/schemas';
import { mm } from '../../domain/units';
import type { BaseParams, SideParams } from '../base';

export const OFFSET_ID = 'offset-grid';
export const OFFSET_VERSION = '1.0';
export const OFFSET_LABEL = 'Offset grid';
export const MAX_ROWS = 10;
export const MAX_DIVIDERS = 4;

export type OffsetPattern = 'brick-bond' | 'alternating' | 'free';

export interface OffsetParams extends BaseParams, SideParams {
  rows: number;
  /** Row clear heights as weights, bottom-up; null = equal rows. */
  rowWeights: number[] | null;
  /** Per row (bottom-up), divider centre-lines over W, left to right, 0–4 each. */
  rowDividerXs: number[][];
  pattern: OffsetPattern;
  /** Brick-bond: full cells per row; alternating: dividers per row. */
  patternCount: number;
  back: 'none' | 'overlay' | 'rebated' | 'per-cell';
  /** Per-cell backs ("<row>-<cell>", 0-based, bottom-up and left to right) when back = per-cell. */
  cellBacks: string[];
  /** Plinth height (ticks), 0 for none. */
  plinth: number;
}

export const DEFAULT_APPEARANCE: Readonly<Appearance> = Object.freeze({ name: 'Offset grid, my version', staging: 'studio', props: true });

const r3 = (v: number) => Math.round(v * 1000) / 1000;

/**
 * Fill the per-row divider lists from a pattern. Brick-bond: even rows split into `count`
 * equal cells, odd rows offset by half a cell. Alternating: `count` dividers per row, shifted a
 * third of a cell left on even rows and mirrored on odd rows. Both raise the count until every cell meets the span limit (`maxCellFrac` of W).
 */
export function patternDividers(pattern: Exclude<OffsetPattern, 'free'>, rows: number, count: number, maxCellFrac = 1): number[][] {
  let m = Math.max(1, Math.round(count));
  for (let guard = 0; guard < 8; guard++) {
    const out: number[][] = [];
    for (let r = 0; r < rows; r++) {
      if (pattern === 'brick-bond') {
        const cells = Math.max(1, Math.min(MAX_DIVIDERS + 1, m));
        out.push(r % 2 === 0
          ? Array.from({ length: cells - 1 }, (_x, i) => r3((i + 1) / cells))
          : Array.from({ length: Math.min(MAX_DIVIDERS, cells) }, (_x, i) => r3((i + 0.5) / cells)));
      } else {
        const k = Math.max(1, Math.min(MAX_DIVIDERS, m));
        const base = Array.from({ length: k }, (_x, i) => r3((i + 1) / (k + 1) - 1 / (3 * (k + 1))));
        out.push(r % 2 === 0 ? base : base.map((x) => r3(1 - x)).reverse());
      }
    }
    const widest = Math.max(...out.map((xs) => {
      const e = [0, ...xs, 1];
      return Math.max(...e.slice(1).map((x, i) => x - e[i]!));
    }));
    if (widest <= maxCellFrac + 1e-9 || m >= MAX_DIVIDERS + 1) return out;
    m++;
  }
  return [];
}

export function defaultOffsetParams(rows = 5): OffsetParams {
  return { rows, rowWeights: null, rowDividerXs: patternDividers('brick-bond', rows, 2), pattern: 'brick-bond', patternCount: 2, back: 'none', cellBacks: [], plinth: 0, base: 'none', sideStyle: 'panel' };
}

export function offsetParamsOf(d: Pick<Design, 'params'>): OffsetParams {
  const p = (d.params ?? {}) as Partial<OffsetParams>;
  return { ...defaultOffsetParams(typeof p.rows === 'number' ? p.rows : 5), ...p };
}

export type OffsetPresetKey = 'brick-bond' | 'alternating' | 'tall-ten';

export const OFFSET_PRESETS: Readonly<Record<OffsetPresetKey, { label: string; description: string; W: number; H: number; D: number; stock: string; params: Partial<OffsetParams> }>> = Object.freeze({
  'brick-bond': { label: 'Brick-bond 1200 × 1800', description: '1200 × 1800 × 300, five rows', W: mm(1200), H: mm(1800), D: mm(300), stock: 'birch-ply-18', params: { rows: 5, pattern: 'brick-bond', patternCount: 2, rowDividerXs: patternDividers('brick-bond', 5, 2) } },
  alternating: { label: 'Alternating 900 × 1600', description: '900 × 1600 × 300, four rows, oak', W: mm(900), H: mm(1600), D: mm(300), stock: 'oak-board-20', params: { rows: 4, pattern: 'alternating', patternCount: 1, rowDividerXs: patternDividers('alternating', 4, 1) } },
  'tall-ten': { label: 'Ten rows 1000 × 2100', description: '1000 × 2100 × 250, ten rows', W: mm(1000), H: mm(2100), D: mm(250), stock: 'birch-ply-18', params: { rows: 10, pattern: 'brick-bond', patternCount: 2, rowDividerXs: patternDividers('brick-bond', 10, 2) } },
});

export function createOffset(key: OffsetPresetKey = 'brick-bond'): Design {
  const p = OFFSET_PRESETS[key] ?? OFFSET_PRESETS['brick-bond'];
  return {
    family: OFFSET_ID, W: p.W, H: p.H, D: p.D, n: 0, even: true, openings: [],
    stockProfileId: p.stock, constructionProfileId: 'offset-grid-joints', appearance: { ...DEFAULT_APPEARANCE },
    params: { ...defaultOffsetParams(p.params.rows ?? 5), ...p.params } as unknown as Record<string, unknown>,
  };
}

const withParams = (d: Design, p: OffsetParams): Design => ({ ...d, appearance: { ...d.appearance }, params: JSON.parse(JSON.stringify(p)) as Record<string, unknown> });

/** Change the row count: equal rows; a pattern refills every row, a free layout repeats its rows. */
export function setRows(d: Design, rows: number, maxCellFrac = 1): Design {
  const p = offsetParamsOf(d);
  const n = Math.max(1, Math.min(MAX_ROWS, Math.round(rows)));
  const rowDividerXs = p.pattern === 'free'
    ? Array.from({ length: n }, (_x, r) => (p.rowDividerXs[r] ?? p.rowDividerXs[r % Math.max(1, p.rowDividerXs.length)] ?? []).slice())
    : patternDividers(p.pattern, n, p.patternCount, maxCellFrac);
  return withParams(d, { ...p, rows: n, rowWeights: null, rowDividerXs, cellBacks: p.cellBacks.filter((k) => Number(k.split('-')[0]) < n) });
}

export function setPattern(d: Design, pattern: OffsetPattern, maxCellFrac = 1): Design {
  const p = offsetParamsOf(d);
  if (pattern === 'free') return withParams(d, { ...p, pattern });
  const count = pattern === 'alternating' ? Math.min(p.patternCount, MAX_DIVIDERS) : p.patternCount;
  return withParams(d, { ...p, pattern, patternCount: count, rowDividerXs: patternDividers(pattern, p.rows, count, maxCellFrac) });
}

export function setOffsetParam<K extends keyof OffsetParams>(d: Design, key: K, value: OffsetParams[K]): Design {
  return withParams(d, { ...offsetParamsOf(d), [key]: value });
}
